import React, { useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../utils/hooks/redux";
import { fetchEstablishmentType } from "../../../../redux/features/establishmentType/establishmentAsyncThunk";
import { selectEstablishmentType } from "../../../../redux/features/establishmentType/establishmentTypeSlice";
import { mergeFilters, clearTicketsByStatus } from "../../../../redux/features/board/columnsSlice";
import { fetchCategories } from "../../../../redux/features/category/categoryAsyncThunk";
import { fetchSubCategories } from "../../../../redux/features/subCategory/subCategoryAsyncThunk";
import { getAllCaseManager } from "../../../../redux/features/user/userAsyncThunk";
import { FilterTabWrapper, FilterButton, StatusFilters, EstablishmentSelectWrapper, EstablishmentSelect, CountBadge } from "./styles";
import { CheckCircleFilled, SearchOutlined, ArrowLeftOutlined, CalendarOutlined, CloseCircleOutlined, EnterOutlined } from "@ant-design/icons";
import { ApartmentOutlined, BankOutlined, CarOutlined, CloudOutlined, DollarCircleOutlined, EnvironmentOutlined, HomeOutlined, RestOutlined, RocketOutlined, SmileOutlined, TrophyOutlined } from "@ant-design/icons";
import { LostStatuses } from "../../../../types/LostStatuses";
import { getStatusTheme } from "../statusThemes";

import { useNavigate, useLocation } from "react-router-dom";
import SystemPayRoutes from "../../../../utils/constants/Routes";
import { Input, Select, Button, Tooltip } from "antd";
import dayjs from "dayjs";
import { columnConfig } from "../const";
import { reportFilterOptions } from "../../../../utils/constants/filterOption";
import DatePickerModal from "../../../../components/DatePickerModal";

type StatusFilterKey = number | "notPaid" | "drafts" | "unpaid" | null;

const boardColumnList = columnConfig.filter((column) => column.isOnBoard && column.status !== undefined && column.status !== null);

const Tabs = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: establishmentTypes, status: establishmentTypeStatus } = useAppSelector(selectEstablishmentType);
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const boardStatusFilter = useAppSelector((state) => state.columns.boardStatusFilter);
  const establishment = useAppSelector((state) => state.users.establishment);
  const totalCounts = useAppSelector((state) => state.columns.counts);
  const { searchValue: reduxSearchValue, searchField: reduxSearchField, dateField, fromDate, toDate } = useAppSelector((state) => state.columns.filters);
  const categories = useAppSelector((state) => (state as any).categories?.data || []);
  const subCategories = useAppSelector((state) => (state as any).subCategories?.data || []);
  const caseManagers = useAppSelector((state) => state.users.caseManagers || []);
  const categoriesStatus = useAppSelector((state) => (state as any).categories?.status);
  const subCategoriesStatus = useAppSelector((state) => (state as any).subCategories?.status);

  // Filter state
  const [searchValue, setSearchValue] = useState<string>(reduxSearchValue || "");
  const [searchField, setSearchField] = useState<string>(reduxSearchField || "lost.trackingId");
  const [submittedSearchValue, setSubmittedSearchValue] = useState<string>(reduxSearchValue || "");
  // When the user picks a sub-category from the cascading dropdown, we keep the visible
  // selector on "Category" but send the API a more specific subCategory.name field.
  const [submittedSearchFieldOverride, setSubmittedSearchFieldOverride] = useState<string | null>(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | undefined>(undefined);
  const [selectedSubCategoryName, setSelectedSubCategoryName] = useState<string | undefined>(undefined);
  const [isDateModalOpen, setIsDateModalOpen] = useState(false);

  useEffect(() => {
    if (establishmentTypeStatus === "idle") {
      dispatch(fetchEstablishmentType());
    }
  }, [dispatch, establishmentTypeStatus]);

  // Lazy-load lookup data the first time one of the dropdown searches becomes relevant
  useEffect(() => {
    if (searchField === "category.name") {
      if (categoriesStatus === "idle") dispatch(fetchCategories());
      // Sub-categories are needed for the cascading sub-cat dropdown shown
      // once a category is picked.
      if (subCategoriesStatus === "idle") dispatch(fetchSubCategories());
    }
    if (searchField === "caseManager.id" && (!caseManagers || caseManagers.length === 0)) {
      dispatch(getAllCaseManager());
    }
  }, [searchField, categoriesStatus, subCategoriesStatus, caseManagers, dispatch]);

  // Apply filters when establishment changes or a search is explicitly submitted
  useEffect(() => {
    const apiSearchField = submittedSearchValue
      ? (submittedSearchFieldOverride || searchField || "lost.trackingId")
      : undefined;
    dispatch(mergeFilters({
      establishmentType: activeFilter === "all" ? null : activeFilter,
      searchValue: submittedSearchValue || undefined,
      searchField: apiSearchField,
    }));

    // Clear tickets to force refetch with new filters across ALL statuses (universal search)
    if (activeFilter || submittedSearchValue) {
      columnConfig.forEach(col => {
        if (col.status !== undefined && col.status !== null) {
          dispatch(clearTicketsByStatus(col.status));
        }
      });
    }
  }, [activeFilter, submittedSearchValue, searchField, dispatch, boardStatusFilter]);

  const handleSearchSubmit = () => {
    setSubmittedSearchValue(searchValue.trim());
  };

  // Date range display
  const formattedDateRange = useMemo(() => {
    return fromDate && toDate ? `${dayjs(fromDate).format("DD-MM-YYYY")} - ${dayjs(toDate).format("DD-MM-YYYY")}` : "Date Range";
  }, [fromDate, toDate]);

  const isDateRangeSelected = useMemo(() => {
    return !!(fromDate && toDate);
  }, [fromDate, toDate]);

  const handleClearDateFilter = () => {
    dispatch(mergeFilters({ dateField: undefined, fromDate: undefined, toDate: undefined }));
    // Clear tickets to force refetch
    boardColumnList.forEach(col => dispatch(clearTicketsByStatus(col.status)));
  };

  const handleDateModalOk = (params: { dateField?: string; fromDate?: string; toDate?: string }) => {
    dispatch(mergeFilters(params));
    setIsDateModalOpen(false);
    // Clear tickets to force refetch with new date filter
    boardColumnList.forEach(col => dispatch(clearTicketsByStatus(col.status)));
  };

  const establishmentTypeOptions = useMemo(
    () =>
      establishmentTypes.map((establishmentType) => ({
        label: establishmentType.name,
        value: establishmentType.id,
        key: establishmentType.id,
        fullUrl: establishmentType.fullUrl,
      })),
    [establishmentTypes]
  );

  const establishmentIconPool = useMemo(
    () => [
      <EnvironmentOutlined style={{ fontSize: 16 }} />,
      <CloudOutlined style={{ fontSize: 16 }} />,
      <BankOutlined style={{ fontSize: 16 }} />,
      <CarOutlined style={{ fontSize: 16 }} />,
      <RocketOutlined style={{ fontSize: 16 }} />,
      <SmileOutlined style={{ fontSize: 16 }} />,
      <TrophyOutlined style={{ fontSize: 16 }} />,
      <ApartmentOutlined style={{ fontSize: 16 }} />,
      <HomeOutlined style={{ fontSize: 16 }} />,
      <RestOutlined style={{ fontSize: 16 }} />,
      <DollarCircleOutlined style={{ fontSize: 16 }} />,
    ],
    []
  );

  type StatusValue = (typeof LostStatuses)[keyof typeof LostStatuses];

  type StatusButtonColors = {
    background?: string;
    border?: string;
    text?: string;
  };

  interface StatusFilterOption {
    key: StatusFilterKey;
    label: string;
    colors?: StatusButtonColors;
    count?: number;
    isBackButton?: boolean;
  }

  // Use real total counts from the backend (stored in state.columns.counts) instead of
  // counting from loaded/paginated tickets which only reflects the currently fetched page.
  const countsByStatus = totalCounts;
  const draftsCount = (totalCounts[LostStatuses.DraftIncomplete] || 0) + (totalCounts[LostStatuses.DraftCompleted] || 0);
  const draftIncompleteCount = totalCounts[LostStatuses.DraftIncomplete] || 0;
  const draftCompleteCount = totalCounts[LostStatuses.DraftCompleted] || 0;
  const notPaidCount = totalCounts[LostStatuses.NotPaid] || 0;
  const lfNotifiedCount = totalCounts[LostStatuses.LFNotified] || 0;
  const overdueCount = totalCounts[LostStatuses.Overdue] || 0;
  const deletedCount = totalCounts[LostStatuses.Deleted] || 0;
  const urgentCount = totalCounts[LostStatuses.Urgent] || 0;

  // Review tab no longer flips dynamically — actionable cards now auto-move to InProcess
  // (which is permanently green-themed), so Review stays its baseline amber to consistently
  // signal "still inside the hold-off window, wait."
  const reviewTabOverride = null;

  // Compute total "All" count for the main board (sum of paid statuses whose column is
  // both flagged as on-board in columnConfig AND not hidden by the per-establishment
  // visibility toggles in Settings → Board Settings). Keeps the "All N" number consistent
  // with what the admin actually sees in the kanban.
  const allBoardCount = useMemo(() => {
    const visibleByStatus: Record<number, boolean> = {
      [LostStatuses.Open]:       (establishment as any)?.showOpenOnBoard       ?? true,
      [LostStatuses.Review]:     (establishment as any)?.showReviewOnBoard     ?? true,
      [LostStatuses.InProcess]:  (establishment as any)?.showInProcessOnBoard  ?? true,
      [LostStatuses.LFNotified]:  establishment?.showLFNotifiedOnBoard         ?? true,
      [LostStatuses.Urgent]:     (establishment as any)?.showUrgentOnBoard     ?? true,
      [LostStatuses.Deleted]:     establishment?.showDeletedOnBoard            ?? false,
    };
    return boardColumnList.reduce((sum, col) => {
      const visible = visibleByStatus[col.status] ?? true; // statuses not in the map (e.g. Pending) keep prior behavior
      return visible ? sum + (totalCounts[col.status] || 0) : sum;
    }, 0);
  }, [totalCounts, establishment]);

  // Determine which page we're on
  const isDraftIncompletePage = location.pathname === "/reports/draft-incomplete";
  const isDraftCompletePage = location.pathname === "/reports/draft-complete";
  const isDraftsPage = location.pathname === "/reports/drafts" || isDraftIncompletePage || isDraftCompletePage;
  const isNotPaidPage = location.pathname === "/reports/not-paid";
  const isUnpaidPage = location.pathname === "/reports/unpaid";
  // Unified "Unpaid" umbrella covers /reports/unpaid, /reports/not-paid, /reports/drafts*
  const isUnpaidUmbrella = isUnpaidPage || isNotPaidPage || isDraftsPage;
  const isLFNotifiedPage = location.pathname === "/reports/lf-notified";
  const isOverduePage = location.pathname === "/reports/overdue";
  const isDeletedPage = location.pathname === "/reports/deleted";
  const isSpecialPage = isUnpaidUmbrella || isLFNotifiedPage || isOverduePage || isDeletedPage;

  const unpaidAllCount = notPaidCount + draftsCount;

  const statusFilterOptions: StatusFilterOption[] = useMemo(() => {
    const getCountForStatus = (statusKey: StatusFilterKey) => {
      if (statusKey === null) {
        return allBoardCount;
      }
      if (statusKey === "unpaid") {
        return unpaidAllCount;
      }
      if (statusKey === "drafts") {
        return draftsCount;
      }
      if (statusKey === "notPaid") {
        return notPaidCount;
      }
      if (statusKey === LostStatuses.Urgent) {
        return urgentCount;
      }
      if (statusKey === LostStatuses.DraftIncomplete) {
        return draftIncompleteCount;
      }
      if (statusKey === LostStatuses.DraftCompleted) {
        return draftCompleteCount;
      }
      if (statusKey === LostStatuses.LFNotified) {
        return lfNotifiedCount;
      }
      if (statusKey === LostStatuses.Overdue) {
        return overdueCount;
      }
      if (statusKey === LostStatuses.Deleted) {
        return deletedCount;
      }
      return countsByStatus[statusKey as number] || 0;
    };

    const mapTheme = (status: StatusValue): StatusFilterOption => {
      const theme = getStatusTheme(status);
      const dynamicColors = status === LostStatuses.Review && reviewTabOverride
        ? reviewTabOverride
        : { background: theme.background, border: theme.border, text: theme.pillColor };
      return {
        key: status,
        label: theme.label,
        colors: dynamicColors,
        count: getCountForStatus(status),
      };
    };

    // Unified Unpaid umbrella: All / Not Paid / Drafts
    if (isUnpaidUmbrella) {
      return [
        { key: null, label: "Home", colors: { background: "#ffffff", border: "#d1d5db", text: "#1f2937" }, count: getCountForStatus(null), isBackButton: true },
        { key: "unpaid", label: "All", colors: { background: "#f3f4f6", border: "#d1d5db", text: "#1f2937" }, count: getCountForStatus("unpaid") },
        { key: "notPaid", label: "Not Paid", colors: { background: "#FEFCE8", border: "#FACC15", text: "#A16207" }, count: getCountForStatus("notPaid") },
        { key: "drafts", label: "Drafts", colors: { background: "#F9FAFB", border: "#D1D5DB", text: "#4B5563" }, count: getCountForStatus("drafts") },
      ];
    }

    if (isLFNotifiedPage) {
      return [
        { key: null, label: "Home", colors: { background: "#ffffff", border: "#d1d5db", text: "#1f2937" }, count: getCountForStatus(null), isBackButton: true },
        mapTheme(LostStatuses.LFNotified),
      ];
    }

    if (isOverduePage) {
      return [
        { key: null, label: "Home", colors: { background: "#ffffff", border: "#d1d5db", text: "#1f2937" }, count: getCountForStatus(null), isBackButton: true },
        mapTheme(LostStatuses.Overdue),
      ];
    }

    if (isDeletedPage) {
      return [
        { key: null, label: "Home", colors: { background: "#ffffff", border: "#d1d5db", text: "#1f2937" }, count: getCountForStatus(null), isBackButton: true },
        mapTheme(LostStatuses.Deleted),
      ];
    }

    // Main reports page tabs — mirrors kanban column visibility from Settings → Board Settings.
    // Order is the same as the kanban (Open → Review → InProcess → LFNotified → Deleted →
    // Urgent). Each pill is gated on its show*OnBoard toggle so hiding a column also hides
    // its filter chip. Defaults match the entity schema (all main-flow ON, Deleted OFF).
    const showOpen       = (establishment as any)?.showOpenOnBoard       ?? true;
    const showReview     = (establishment as any)?.showReviewOnBoard     ?? true;
    const showInProcess  = (establishment as any)?.showInProcessOnBoard  ?? true;
    const showLFNotified =  establishment?.showLFNotifiedOnBoard         ?? true;
    const showUrgent     = (establishment as any)?.showUrgentOnBoard     ?? true;
    const showDeleted    =  establishment?.showDeletedOnBoard            ?? false;

    const mainTabs: StatusFilterOption[] = [
      { key: null, label: "All", colors: { background: "#ffffff", border: "#d1d5db", text: "#1f2937" }, count: boardStatusFilter === null ? getCountForStatus(null) : undefined },
    ];
    if (showOpen)       mainTabs.push(mapTheme(LostStatuses.Open));
    if (showReview)     mainTabs.push(mapTheme(LostStatuses.Review));
    if (showInProcess)  mainTabs.push(mapTheme(LostStatuses.InProcess));
    if (showLFNotified) mainTabs.push(mapTheme(LostStatuses.LFNotified));
    if (showDeleted)    mainTabs.push(mapTheme(LostStatuses.Deleted));
    if (showUrgent)     mainTabs.push(mapTheme(LostStatuses.Urgent));
    return mainTabs;
  }, [countsByStatus, allBoardCount, urgentCount, notPaidCount, unpaidAllCount, draftsCount, draftIncompleteCount, draftCompleteCount, lfNotifiedCount, overdueCount, deletedCount, isUnpaidUmbrella, isLFNotifiedPage, isOverduePage, isDeletedPage, boardStatusFilter, establishment]);

  const handleStatusFilterChange = (value: StatusFilterKey) => {
    // Map status values to URL paths
    const statusToPathMap: Record<string, string> = {
      "null": SystemPayRoutes.reports,
      "unpaid": `${SystemPayRoutes.reports}/unpaid`,
      "drafts": `${SystemPayRoutes.reports}/drafts`,
      "notPaid": `${SystemPayRoutes.reports}/not-paid`,
      [LostStatuses.Urgent]: `${SystemPayRoutes.reports}/urgent`,
      [LostStatuses.Open]: `${SystemPayRoutes.reports}/open`,
      [LostStatuses.Pending]: `${SystemPayRoutes.reports}/pending`,
      [LostStatuses.Review]: `${SystemPayRoutes.reports}/review`,
      [LostStatuses.InProcess]: `${SystemPayRoutes.reports}/in-process`,
      [LostStatuses.LFNotified]: `${SystemPayRoutes.reports}/lf-notified`,
      [LostStatuses.DraftIncomplete]: `${SystemPayRoutes.reports}/draft-incomplete`,
      [LostStatuses.DraftCompleted]: `${SystemPayRoutes.reports}/draft-complete`,
      [LostStatuses.Overdue]: `${SystemPayRoutes.reports}/overdue`,
      [LostStatuses.Deleted]: `${SystemPayRoutes.reports}/deleted`,
    };

    // Get the target path
    const key = value === null ? "null" : value.toString();
    const targetPath = statusToPathMap[key];

    // Navigate to the appropriate URL
    if (targetPath) {
      navigate(targetPath);
    }
  };

  const establishmentSelectOptions = useMemo(() => {
    const mappedOptions = establishmentTypeOptions.map((filter: any) => ({
      label: filter.label,
      value: filter.key,
    }));
    return [{ label: "All Establishments", value: "all" }, ...mappedOptions];
  }, [establishmentTypeOptions]);

  const handleEstablishmentChange = (value: string) => {
    setActiveFilter(value === "all" ? null : value);
  };

  // Drafts tab is "active" when on /reports/drafts or one of its sub-status filters
  const isDraftsActive =
    boardStatusFilter === "drafts" ||
    boardStatusFilter === LostStatuses.DraftIncomplete ||
    boardStatusFilter === LostStatuses.DraftCompleted;
  const showDraftSubTabs = isUnpaidUmbrella && isDraftsActive;

  const draftSubTabs: StatusFilterOption[] = [
    { key: "drafts", label: "All Drafts", colors: { background: "#F9FAFB", border: "#D1D5DB", text: "#4B5563" }, count: draftsCount },
    {
      key: LostStatuses.DraftIncomplete,
      label: getStatusTheme(LostStatuses.DraftIncomplete).label,
      colors: {
        background: getStatusTheme(LostStatuses.DraftIncomplete).background,
        border: getStatusTheme(LostStatuses.DraftIncomplete).border,
        text: getStatusTheme(LostStatuses.DraftIncomplete).pillColor,
      },
      count: draftIncompleteCount,
    },
    {
      key: LostStatuses.DraftCompleted,
      label: getStatusTheme(LostStatuses.DraftCompleted).label,
      colors: {
        background: getStatusTheme(LostStatuses.DraftCompleted).background,
        border: getStatusTheme(LostStatuses.DraftCompleted).border,
        text: getStatusTheme(LostStatuses.DraftCompleted).pillColor,
      },
      count: draftCompleteCount,
    },
  ];

  return (
    <>
    <FilterTabWrapper>
      <StatusFilters>
        {statusFilterOptions.map((statusFilter) => {
          const isActive = boardStatusFilter === statusFilter.key;
          const countValue = typeof statusFilter.count === "number" ? statusFilter.count : null;
          const shouldShowCount = typeof countValue === "number" && countValue > 0;
          const chipTextColor = statusFilter.colors?.text || "#1f2937";
          const chipBackground = statusFilter.colors?.background || "#ffffff";
          const isBackButton = statusFilter.isBackButton;
          return (
            <FilterButton
              key={`status-${statusFilter.key ?? "all"}`}
              active={isActive}
              onClick={() => handleStatusFilterChange(statusFilter.key)}
              $backgroundColor={statusFilter.colors?.background}
              $borderColor={statusFilter.colors?.border}
              $textColor={statusFilter.colors?.text}
            >
              {isBackButton && <ArrowLeftOutlined style={{ marginRight: 4 }} />}
              {isActive && !isBackButton && <CheckCircleFilled style={{ marginRight: 4, color: chipBackground }} />}
              <span style={{ fontSize: "14px", fontWeight: 600 }}>{statusFilter.label}</span>
              {shouldShowCount && !isBackButton && (
                <CountBadge $textColor={isActive ? "#ffffff" : chipTextColor} className="status-count-badge">
                  {countValue}
                </CountBadge>
              )}
            </FilterButton>
          );
        })}
      </StatusFilters>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "nowrap", flexShrink: 0 }}>
        <Select
          value={searchField}
          style={{ minWidth: 150 }}
          options={reportFilterOptions}
          onChange={(value) => {
            const next = (value as string) || "lost.trackingId";
            setSearchField(next);
            // Reset value state when switching field types so stale values don't leak
            setSearchValue("");
            setSubmittedSearchValue("");
            setSelectedCategoryId(undefined);
            setSelectedSubCategoryName(undefined);
            setSubmittedSearchFieldOverride(null);
          }}
          placeholder="Claim Number"
          size="middle"
          listHeight={500}
        />
        {searchField === "caseManager.id" ? (
          <Select
            showSearch
            placeholder="Select case manager"
            value={submittedSearchValue || undefined}
            onChange={(val) => setSubmittedSearchValue((val as string) || "")}
            options={(caseManagers || []).map((cm: any) => ({ label: `${cm.firstName ?? ""} ${cm.lastName ?? ""}`.trim(), value: cm.id }))}
            allowClear
            style={{ width: 260 }}
            filterOption={(input, opt) => (opt?.label as string ?? "").toLowerCase().includes(input.toLowerCase())}
          />
        ) : searchField === "category.name" ? (
          <>
            <Select
              showSearch
              placeholder="Select category"
              value={selectedCategoryId}
              onChange={(val, opt: any) => {
                setSelectedCategoryId(val as string);
                // Reset any previous sub-category selection when category changes
                setSelectedSubCategoryName(undefined);
                setSubmittedSearchFieldOverride(null);
                setSubmittedSearchValue(opt?.name || "");
              }}
              options={(categories || []).map((c: any) => ({ label: c.name, value: c.id, name: c.name }))}
              allowClear
              style={{ width: 220 }}
              filterOption={(input, opt) => (opt?.label as string ?? "").toLowerCase().includes(input.toLowerCase())}
            />
            {selectedCategoryId && (
              <Select
                showSearch
                placeholder="Sub Category (optional)"
                value={selectedSubCategoryName}
                onChange={(val) => {
                  setSelectedSubCategoryName(val as string | undefined);
                  if (val) {
                    setSubmittedSearchFieldOverride("subCategory.name");
                    setSubmittedSearchValue(val as string);
                  } else {
                    // Cleared sub-category — fall back to the selected category
                    setSubmittedSearchFieldOverride(null);
                    const cat = (categories || []).find((c: any) => c.id === selectedCategoryId);
                    setSubmittedSearchValue(cat?.name || "");
                  }
                }}
                options={(subCategories || [])
                  .filter((s: any) => s?.category?.id === selectedCategoryId)
                  .map((s: any) => ({ label: s.name, value: s.name }))}
                allowClear
                style={{ width: 220 }}
                notFoundContent={subCategoriesStatus === "loading" ? "Loading…" : "No sub-categories"}
                filterOption={(input, opt) => (opt?.label as string ?? "").toLowerCase().includes(input.toLowerCase())}
              />
            )}
          </>
        ) : (
          <Input
            placeholder="Press Enter to search"
            prefix={<SearchOutlined />}
            suffix={
              <Tooltip title="Press Enter to search">
                <EnterOutlined style={{ color: "#9ca3af" }} />
              </Tooltip>
            }
            value={searchValue}
            onChange={(e) => {
              const next = e.target.value;
              setSearchValue(next);
              // If the user clears the field, immediately drop the active search
              if (next === "" && submittedSearchValue) {
                setSubmittedSearchValue("");
              }
            }}
            onPressEnter={handleSearchSubmit}
            allowClear
            style={{ width: 260 }}
          />
        )}
        <Tooltip title={isDateRangeSelected ? formattedDateRange : "Select date range"}>
          <Button
            icon={<CalendarOutlined />}
            onClick={() => setIsDateModalOpen(true)}
            type={isDateRangeSelected ? "primary" : "default"}
            style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 4, height: 32, minWidth: 32, padding: "0 8px" }}
          >
            {isDateRangeSelected && (
              <CloseCircleOutlined
                style={{ marginLeft: 4 }}
                onClick={(e) => { e.stopPropagation(); handleClearDateFilter(); }}
              />
            )}
          </Button>
        </Tooltip>
        <EstablishmentSelectWrapper style={{ minWidth: 160 }}>
          <EstablishmentSelect
            value={activeFilter ?? "all"}
            onChange={(value) => handleEstablishmentChange(value as string)}
            options={establishmentSelectOptions}
            popupMatchSelectWidth={false}
          />
        </EstablishmentSelectWrapper>
      </div>
      <DatePickerModal
        isModalOpen={isDateModalOpen}
        handleOk={handleDateModalOk}
        handleCancel={() => setIsDateModalOpen(false)}
        values={{ dateField, fromDate, toDate }}
      />
    </FilterTabWrapper>
    {showDraftSubTabs && (
      <FilterTabWrapper style={{ marginTop: 6 }}>
        <StatusFilters>
          {draftSubTabs.map((statusFilter) => {
            const isActive = boardStatusFilter === statusFilter.key;
            const countValue = typeof statusFilter.count === "number" ? statusFilter.count : null;
            const shouldShowCount = typeof countValue === "number" && countValue > 0;
            const chipTextColor = statusFilter.colors?.text || "#1f2937";
            const chipBackground = statusFilter.colors?.background || "#ffffff";
            return (
              <FilterButton
                key={`draft-sub-${statusFilter.key}`}
                active={isActive}
                onClick={() => handleStatusFilterChange(statusFilter.key)}
                $backgroundColor={statusFilter.colors?.background}
                $borderColor={statusFilter.colors?.border}
                $textColor={statusFilter.colors?.text}
              >
                {isActive && <CheckCircleFilled style={{ marginRight: 4, color: chipBackground }} />}
                <span style={{ fontSize: "13px", fontWeight: 600 }}>{statusFilter.label}</span>
                {shouldShowCount && (
                  <CountBadge $textColor={isActive ? "#ffffff" : chipTextColor} className="status-count-badge">
                    {countValue}
                  </CountBadge>
                )}
              </FilterButton>
            );
          })}
        </StatusFilters>
      </FilterTabWrapper>
    )}
    </>
  );
};

export default Tabs;
